import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '../base-dialog';

const MAX_VISIBLE_ITEMS = 5;

export const BulkDeleteDialog = <T extends { id: string | number }>({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  title,
  description,
  items,
  listHeader,
  renderItem,
  renderMoreLabel,
  confirmLabel,
  cancelLabel,
}: {
  open: boolean;
  onOpenChange: (isDialogOpen: boolean) => void;
  onConfirm: () => void | Promise<void>;
  isPending?: boolean;
  title: string;
  description: ReactNode;
  items: T[];
  listHeader?: ReactNode;
  renderItem: (item: T) => ReactNode;
  renderMoreLabel?: (remainingCount: number) => ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
}) => {
  const { t } = useTranslation();

  const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS);
  const remainingCount = items.length - MAX_VISIBLE_ITEMS;

  return (
    <BaseDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      intent="destructive"
      size="sm"
      isPending={isPending}
      primaryAction={{
        label: confirmLabel ?? t(($) => $.common.delete),
        onClick: onConfirm,
      }}
      secondaryAction={{
        label: cancelLabel ?? t(($) => $.common.cancel),
        onClick: () => onOpenChange(false),
      }}
    >
      <div className="mt-4">
        {listHeader}
        {visibleItems.map((item) => (
          <Fragment key={item.id}>{renderItem(item)}</Fragment>
        ))}
        {remainingCount > 0 && renderMoreLabel?.(remainingCount)}
      </div>
    </BaseDialog>
  );
};
